import React, { FC, useCallback, useEffect, useRef, useState } from 'react';

import { ReloadOutlined, ClockCircleOutlined, CheckOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';

import styles from './AutoReloadButton.module.scss';

const RELOAD_OPTIONS = [
  { label: '1 минута', value: 1 },
  { label: '5 минут', value: 5 },
  { label: '10 минут', value: 10 },
  { label: '15 минут', value: 15 },
  { label: '30 минут', value: 30 },
  { label: '1 час', value: 60 },
];

interface AutoReloadButtonProps {
  isCompact?: boolean;
  onRefresh?: () => void;
}

const AutoReloadButton: FC<AutoReloadButtonProps> = ({ isCompact = false, onRefresh }) => {
  const [ intervalMinutes, setIntervalMinutes ] = useState<number | null>(null);
  const [ secondsLeft, setSecondsLeft ] = useState(0);
  const [ isOpen, setIsOpen ] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;

  // Закрытие dropdown при клике вне
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [ isOpen ]);

  // Запуск / остановка таймера
  const startTimer = useCallback((minutes: number) => {
    // Очищаем предыдущие таймеры
    if (timerRef.current) clearInterval(timerRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);

    setIntervalMinutes(minutes);
    setSecondsLeft(minutes * 60);
    setIsOpen(false);

    // Обратный отсчёт
    countdownRef.current = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          return minutes * 60;
        }
        return prev - 1;
      });
    }, 1000);

    // Таймер обновления данных
    timerRef.current = setInterval(() => {
      if (onRefreshRef.current) {
        onRefreshRef.current();
      }
    }, minutes * 60 * 1000);
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    timerRef.current = null;
    countdownRef.current = null;
    setIntervalMinutes(null);
    setSecondsLeft(0);
    setIsOpen(false);
  }, []);

  // Cleanup при размонтировании
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, []);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const isActive = intervalMinutes !== null;

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <Tooltip title={isActive ? `Обновление данных через ${formatTime(secondsLeft)}` : 'Автоматическое обновление данных'}>
        <button
          data-analytics-id="dashboard-auto-refresh"
          className={`${styles.reloadButton} ${isActive ? styles.active : ''}`}
          onClick={() => setIsOpen(!isOpen)}
        >
          {isActive ? <ReloadOutlined /> : <ClockCircleOutlined />}
          {isActive && <span className={styles.countdown}>{formatTime(secondsLeft)}</span>}
          {!isCompact && !isActive && <span>Таймер</span>}
        </button>
      </Tooltip>

      {isOpen && (
        <div className={styles.dropdown}>
          <div className={styles.dropdownLabel}>Обновление данных через</div>
          {RELOAD_OPTIONS.map((option) => (
            <button
              key={option.value}
              className={`${styles.dropdownItem} ${intervalMinutes === option.value ? styles.selected : ''}`}
              onClick={() => startTimer(option.value)}
            >
              <span>{option.label}</span>
              {intervalMinutes === option.value && <CheckOutlined style={{ fontSize: 12 }} />}
            </button>
          ))}
          {isActive && (
            <>
              <div className={styles.divider} />
              <button
                className={styles.dropdownItem}
                onClick={stopTimer}
                style={{ color: '#ff4d4f' }}
              >
                Отключить
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AutoReloadButton;
